<template>
  <aside class="ai-history" :class="{ 'ai-history--docked': docked }">
    <div class="ai-history__head">
      <span class="ai-history__title">History</span>
      <button type="button" class="ai-history__close" title="Close history" @click="$emit('close')">
        <i class="fa fa-angle-double-right"></i>
      </button>
    </div>

    <button type="button" class="ai-history__new" @click="$emit('new')">
      <i class="fa fa-plus"></i> New conversation
    </button>

    <div class="ai-history__list">
      <p v-if="loading" class="ai-history__empty">Loading…</p>

      <p v-else-if="!conversations.length" class="ai-history__empty">
        Nothing here yet. Conversations appear once you send a message.
      </p>

      <div
        v-for="conversation in visibleConversations"
        :key="conversation.id"
        class="ai-history__row"
        :class="{ 'is-active': conversation.id === activeId }"
        @click="onOpen(conversation)"
      >
        <template v-if="renamingId === conversation.id">
          <input
            ref="renameInput"
            v-model="renameDraft"
            class="ai-history__rename"
            type="text"
            maxlength="120"
            @click.stop
            @keydown.enter.prevent="commitRename(conversation)"
            @keydown.esc.prevent="cancelRename"
            @blur="commitRename(conversation)"
          />
        </template>

        <template v-else>
          <div class="ai-history__label">{{ conversation.label }}</div>
          <div class="ai-history__meta">
            {{ relativeTime(conversation.lastActivityAt) }}
            <span v-if="conversation.messageCount">· {{ conversation.messageCount }}</span>
          </div>

          <div class="ai-history__actions">
            <button
              type="button"
              title="Rename"
              @click.stop="startRename(conversation)"
            >
              <i class="fa fa-pencil"></i>
            </button>
            <button
              type="button"
              title="Remove from this list"
              @click.stop="$emit('hide', conversation)"
            >
              <i class="fa fa-trash-o"></i>
            </button>
          </div>
        </template>
      </div>
    </div>

    <!-- Pinned below the list rather than in the ARCH nav: settings belong to the
         workspace, and the people holding either admin grant are a handful — a permanent
         nav entry for them would sit in everyone's way in the markup for a screen almost
         nobody opens. -->
    <button
      v-if="canManageWorkspace"
      type="button"
      class="ai-history__settings"
      title="AI Workspace settings"
      @click="$emit('settings')"
    >
      <i class="fa fa-cog"></i> Settings
    </button>
  </aside>
</template>

<script>
import moment from "moment";

export default {
  name: "ConversationHistoryPanel",

  props: {
    conversations: {
      type: Array,
      default: () => [],
    },
    activeId: {
      type: String,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Docked sits in the layout beside the conversation and never covers it.
     * Undocked, it overlays the transcript and is opened from the menu.
     */
    docked: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      renamingId: null,
      renameDraft: "",
    };
  },

  computed: {
    visibleConversations() {
      return this.loading ? [] : this.conversations;
    },

    /**
     * Either admin grant, both narrower than `ai.chat.use` — most people in the workspace
     * never see this. `ai.skill.manage` is here because the Skills tab lives on the same
     * screen, and an author with no way to reach it would be writing nothing.
     *
     * Hiding it is a courtesy, not a boundary: the route guard and every admin endpoint
     * check the same permissions server-side.
     */
    canManageWorkspace() {
      return this.$store.getters["authpermission/hasAnyPermission"]([
        "ai.config.manage",
        "ai.skill.manage",
      ]);
    },
  },

  methods: {
    relativeTime(value) {
      return value ? moment(value).fromNow() : "";
    },

    onOpen(conversation) {
      if (this.renamingId === conversation.id) {
        return;
      }
      this.$emit("open", conversation);
    },

    startRename(conversation) {
      this.renamingId = conversation.id;
      // The stored title, not the derived label — otherwise renaming a conversation
      // that never had one would silently promote "Grill me — 5 Aug" into a real
      // title the user did not write.
      this.renameDraft = conversation.title || "";
      this.$nextTick(() => {
        const input = Array.isArray(this.$refs.renameInput)
          ? this.$refs.renameInput[0]
          : this.$refs.renameInput;
        if (input) {
          input.focus();
          input.select();
        }
      });
    },

    cancelRename() {
      this.renamingId = null;
      this.renameDraft = "";
    },

    commitRename(conversation) {
      if (this.renamingId !== conversation.id) {
        return;
      }

      const title = this.renameDraft.trim();
      this.cancelRename();

      if (title && title !== conversation.title) {
        this.$emit("rename", { conversation, title });
      }
    },
  },
};
</script>

<style scoped>
/* Pinned to the right edge for the full height of the workspace, mirroring the
   ARCH nav on the left. */
.ai-history {
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  z-index: 5;
  width: 280px;
  display: flex;
  flex-direction: column;
  border-left: 1px solid #e3e6ec;
  background: #fff;
}

/* Undocked it is covering the conversation, so it earns a shadow. Docked, the
   workspace has reserved its width and it is covering nothing. */
.ai-history:not(.ai-history--docked) {
  box-shadow: -8px 0 24px rgba(24, 30, 44, 0.1);
}

.ai-history__head {
  display: flex;
  align-items: center;
  padding: 14px 12px 10px 16px;
}

.ai-history__title {
  flex: 1 1 auto;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #8a91a0;
}

.ai-history__close {
  border: 0;
  background: none;
  color: #8a91a0;
  cursor: pointer;
}

.ai-history__new {
  margin: 0 12px 10px;
  padding: 8px 10px;
  border: 1px dashed #d7dce5;
  border-radius: 8px;
  background: none;
  color: #5b6b8c;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.ai-history__new:hover {
  border-color: #b7c0d0;
  background: #f2f4f8;
}

.ai-history__list {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 0 8px 12px;
}

.ai-history__settings {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 11px 16px;
  border: none;
  border-top: 1px solid #e3e6ec;
  background: none;
  color: #8a91a0;
  font-size: 13px;
  text-align: left;
  cursor: pointer;
}

.ai-history__settings:hover {
  background: #f2f4f8;
  color: #5b6b8c;
  text-decoration: none;
}

.ai-history__empty {
  padding: 10px 8px;
  font-size: 12px;
  line-height: 1.5;
  color: #a0a6b3;
}

.ai-history__row {
  position: relative;
  padding: 9px 10px;
  border-radius: 8px;
  cursor: pointer;
}

.ai-history__row:hover {
  background: #f2f4f8;
}

.ai-history__row.is-active {
  background: #e9eef7;
}

.ai-history__label {
  font-size: 13px;
  line-height: 1.4;
  color: #2f3542;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.ai-history__meta {
  margin-top: 2px;
  font-size: 11px;
  color: #a0a6b3;
}

.ai-history__actions {
  position: absolute;
  top: 6px;
  right: 6px;
  display: none;
  gap: 2px;
}

.ai-history__row:hover .ai-history__actions {
  display: flex;
}

.ai-history__actions button {
  width: 24px;
  height: 24px;
  border: 0;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  color: #8a91a0;
  font-size: 11px;
  cursor: pointer;
}

.ai-history__actions button:hover {
  color: #2f3542;
}

.ai-history__rename {
  width: 100%;
  padding: 4px 6px;
  border: 1px solid #b7c0d0;
  border-radius: 6px;
  font-size: 13px;
}

/* Dark theme ------------------------------------------------------------- */

body.full-dark .ai-history {
  border-left-color: #333c50;
  background: #1e2431;
}

body.full-dark .ai-history__row:hover {
  background: #262d3d;
}

body.full-dark .ai-history__row.is-active {
  background: #2b3243;
}

body.full-dark .ai-history__label {
  color: #dfe3ec;
}

body.full-dark .ai-history__new {
  border-color: #333c50;
  color: #9aa6c0;
}

body.full-dark .ai-history__settings {
  border-top-color: #333c50;
  color: #8894ad;
}

body.full-dark .ai-history__settings:hover {
  background: #262d3d;
  color: #cfd6e6;
}

body.full-dark .ai-history__actions button {
  background: rgba(35, 41, 56, 0.9);
}
</style>
